import { combineEpics, ofType } from "redux-observable";
import { mergeMap } from "rxjs/operators";
import { of, EMPTY } from "rxjs";
import queueSendEffect from "./queue-send-effect";
import {
	onlineStatusPollEffect,
	checkNetworkEffect,
	browserOnlineOfflineEffect,
	setInitialNetworkEffect,
} from "./online-status-poll-effect";
import { setGlobalMessage } from "features/jobs";
import { sendRequested } from "../actions";
import queueTypes from "../actions/types";
import restoreOfflineQueueAfterLoginEffect from "./restore-offline-queue-after-login-effect";

// Clear any stale global sync messages when navigating or after rehydrate if there are no queued items
const clearStaleGlobalMessagesEffect = (action$, state$) =>
    action$.pipe(
        ofType("@@router/LOCATION_CHANGE", "persist/REHYDRATE"),
        mergeMap(() => {
            const hasNoQueue = !state$.value.queue?.list || state$.value.queue.list.length === 0;
            const hasMessages = !!state$.value.jobs?.globalMessage && state$.value.jobs.globalMessage.length > 0;
            if (hasNoQueue && hasMessages) {
                return of(setGlobalMessage([]));
            }
            return EMPTY;
        })
    );

// Keep global messages exactly in sync with current queue items
const reconcileGlobalMessagesEffect = (action$, state$) =>
    action$.pipe(
        ofType(
            queueTypes.QUEUE_SEND_SUCCEEDED,
            queueTypes.QUEUE_SEND_FAILED,
            queueTypes.QUEUE_ITEM_ADDED,
            queueTypes.QUEUE_NETWORK_CHANGED,
            queueTypes.QUEUE_SEND_REQUESTED
        ),
        mergeMap(() => {
            const queueList = state$.value.queue?.list || [];
            const allowedIds = queueList.map((q) => q.id);
            const current = state$.value.jobs?.globalMessage || [];
            const next = current.filter((pair) => allowedIds.indexOf(pair[0]) !== -1);
            if (next.length !== current.length) {
                return of(setGlobalMessage(next));
            }
            return EMPTY;
        })
    );

// Kick the queue processor on rehydrate/navigation if there are pending items and we are online
const triggerQueueProcessingEffect = (action$, state$) =>
    action$.pipe(
        ofType("persist/REHYDRATE", "@@router/LOCATION_CHANGE"),
        mergeMap(() => {
            const hasPending = !!state$.value.queue?.list && state$.value.queue.list.length > 0;
            const online = !!state$.value.queue?.hasNetwork;
            if (hasPending && online) {
                return of(sendRequested());
            }
            return EMPTY;
        })
    );

// Notify users when items permanently fail or exceed max retries
const notifyPermanentFailureEffect = (action$, state$) =>
    action$.pipe(
        ofType(queueTypes.QUEUE_SEND_FAILED),
        mergeMap((action) => {
            // Handle both old format (just id) and new format (object)
            const payload = action.payload;
            const id = typeof payload === 'string' ? payload : (payload?.id || payload);
            const isPermanent = typeof payload === 'string' ? false : (payload?.isPermanent || false);
            
            if (isPermanent) {
                const failedItem = state$.value.queue?.list?.find((item) => item && item.id === id);
                if (failedItem) {
                    const isTimesheet = failedItem.url && /\/timesheet\//i.test(failedItem.url);
                    const lastError = failedItem.lastError || null;
                    const message = isTimesheet 
                        ? `Timesheet sync failed permanently. ${lastError || "Please check your connection and try again."}`
                        : `Sync failed permanently. ${lastError || "Please check your connection and try again."}`;
                    
                    // Add to global messages for persistent notification
                    const currentMessages = state$.value.jobs?.globalMessage || [];
                    const existingIndex = currentMessages.findIndex((pair) => pair[0] === id);
                    const newMessage = [id, message];
                    
                    if (existingIndex >= 0) {
                        currentMessages[existingIndex] = newMessage;
                    } else {
                        currentMessages.push(newMessage);
                    }
                    
                    return of(setGlobalMessage([...currentMessages]));
                }
            }
            return EMPTY;
        })
    );

const queueEffects = combineEpics(
    queueSendEffect,
    onlineStatusPollEffect,
    checkNetworkEffect,
    browserOnlineOfflineEffect,
    setInitialNetworkEffect,
    clearStaleGlobalMessagesEffect,
    reconcileGlobalMessagesEffect,
    triggerQueueProcessingEffect,
    notifyPermanentFailureEffect,
    restoreOfflineQueueAfterLoginEffect
);

export default queueEffects;

/*

Requirements
------------

* When the app comes online check network status first before everything else fires
* Every x seconds check network status
* When network goes offline disable all list requests
*

*/
